import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { submitLeadToZoho } from "@/lib/zoho";

const CONSENT_TEXT =
  "I agree that Rao Investment Solutions may contact me by phone, email or WhatsApp about this enquiry. I can withdraw consent at any time. My details are handled per the";

const STORE_KEY = "ris-lead";

// Remembers the visitor for this tab so a second form (report, then e-book)
// comes pre-filled. The form is always shown, so every download is a visible lead.
export const rememberedLead = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
  } catch {
    return null;
  }
};

const remember = (lead) => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(lead));
  } catch {
    /* storage blocked: the visitor just gets asked again */
  }
};

// Sends one lead to Zoho tagged with the goal and lead magnet.
export async function sendGoalLead({ lead, goal, magnet, result }) {
  const params = new URLSearchParams(window.location.search);
  await submitLeadToZoho({
    ...lead,
    // "Goal: X | result" — the workflow matches on "Goal: X" and the visitor-facing
    // email shows this line; internal details stay in the message field.
    interest: result?.summary ? `Goal: ${goal.name} | ${result.summary}` : `Goal: ${goal.name}`,
    audience: goal.audience,
    calculator: `${goal.name} goal planner`,
    calculator_result: result?.summary,
    message: `Lead magnet: ${magnet}`,
    source_page: window.location.pathname,
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    consent: true,
    consent_text: CONSENT_TEXT,
  });
}

// Short name/phone/email form that gates a download.
export default function GateForm({ goal, magnet, result, cta, onUnlocked, dark = false, testid = "gate-form" }) {
  const [form, setForm] = useState(() => {
    const known = rememberedLead();
    return { name: known?.name || "", phone: known?.phone || "", email: known?.email || "" };
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || form.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter your name, a valid email and a 10-digit phone number.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to be contacted so we can send this to you.");
      return;
    }
    setLoading(true);
    try {
      await sendGoalLead({ lead: form, goal, magnet, result });
    } catch {
      // Still deliver the download; the lead may need a manual follow-up.
    }
    remember(form);
    setLoading(false);
    onUnlocked(form);
  };

  const input = `w-full px-4 py-3 font-sans text-sm bg-transparent border focus-visible:outline-none focus-visible:border-orange transition-colors ${
    dark ? "border-white/25 text-white placeholder:text-white/40" : "border-navy/15 text-offblack placeholder:text-slate/60"
  }`;
  const label = `font-sans text-xs uppercase tracking-[0.18em] mb-2 block ${dark ? "text-white/60" : "text-slate"}`;

  return (
    <form onSubmit={submit} data-testid={testid} noValidate>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={label} htmlFor={`${testid}-name`}>Name</label>
          <input id={`${testid}-name`} className={input} value={form.name} onChange={set("name")} autoComplete="name" data-testid={`${testid}-name`} />
        </div>
        <div>
          <label className={label} htmlFor={`${testid}-phone`}>Phone / WhatsApp</label>
          <input id={`${testid}-phone`} className={input} value={form.phone} onChange={set("phone")} inputMode="tel" autoComplete="tel" placeholder="+91" data-testid={`${testid}-phone`} />
        </div>
        <div>
          <label className={label} htmlFor={`${testid}-email`}>Email</label>
          <input id={`${testid}-email`} className={input} type="email" value={form.email} onChange={set("email")} autoComplete="email" data-testid={`${testid}-email`} />
        </div>
      </div>
      <label className={`mt-5 flex items-start gap-3 cursor-pointer font-sans text-xs leading-relaxed ${dark ? "text-white/60" : "text-slate"}`}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 shrink-0 accent-orange" data-testid={`${testid}-consent`} />
        <span>
          {CONSENT_TEXT}{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-orange">Privacy Policy</Link>.
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange text-white font-sans text-sm font-medium shadow-elevated hover:bg-orange-dark hover:shadow-citrine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-citrine focus-visible:ring-offset-2 active:translate-y-px transition-[background-color,box-shadow,transform] duration-300 disabled:opacity-60 group"
        data-testid={`${testid}-submit`}
      >
        <Lock className="w-4 h-4" strokeWidth={1.5} />
        {loading ? "Preparing…" : cta}
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
      </button>
    </form>
  );
}
